/*
 * Whether the browser asking for lyrics is in Japan (ENG-415).
 *
 * Musixmatch's go-live checklist makes copy and paste of lyrics something
 * that must be disabled "for traffic originating in Japan" — a territory
 * obligation, not a language one. The signal it describes is the viewer's IP
 * country, which a browser cannot see: the deployment sits behind CloudFront,
 * which knows it (`CloudFront-Viewer-Country`), but nothing forwards it to
 * the client today and the lyrics endpoint (ENG-414) returns nothing about
 * where the reader is. Passing it through is the right fix and belongs on the
 * backend; until then this is decided in the browser, from the two location
 * signals a page has without asking anyone:
 *
 *   - the **resolved IANA time zone** (`Asia/Tokyo`, or the `Japan` link name
 *     some systems still resolve to). This is the primary signal: it reflects
 *     the machine's clock setting rather than what the user reads in.
 *   - an explicit **`JP` region subtag** on any accepted language
 *     (`ja-JP`, `en-JP`). A bare `ja` is deliberately *not* enough — Japanese
 *     is read outside Japan, and the obligation is about where the traffic is.
 *
 * Both are trivially spoofable and a VPN defeats either, which is fine: the
 * obligation is to disable the affordance, not to defeat a determined copier
 * (the words are in the DOM either way). What matters is the direction of the
 * error, so the signals are OR-ed — a reader in Tokyo whose browser is set to
 * English is still covered, at the cost of also covering someone abroad whose
 * clock is on JST.
 *
 * Read at render time rather than module load: a `vi.stubGlobal` in a test,
 * and a laptop that crosses a time zone, both want to be noticed.
 */

/** IANA zone identifiers that mean Japan, including the legacy link name. */
const JAPAN_TIME_ZONES = new Set(['Asia/Tokyo', 'Japan']);

/** The browser's resolved time zone, or '' where `Intl` cannot say. */
const resolvedTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

/** Every language tag the browser would send, most preferred first. */
const acceptedLanguages = () => {
  try {
    const { languages, language } = typeof navigator === 'undefined' ? {} : navigator;
    if (Array.isArray(languages) && languages.length > 0) return languages;
    return language ? [language] : [];
  } catch {
    return [];
  }
};

/**
 * A `JP` region subtag on a BCP 47 tag (`ja-JP`, `en-JP-x-foo`, and
 * `ja-Jpan-JP` — a script subtag may sit between the language and the
 * region, so the region is not simply the second subtag). Read by position
 * in the tag's grammar rather than searched for anywhere in the string, so a
 * variant or private-use subtag that happens to read "jp" does not count.
 */
const hasJapanRegion = (tag) => {
  const [, ...rest] = String(tag).split('-');
  // Skip an optional 4-letter script subtag; the region is what follows.
  const region = rest.length > 0 && /^[A-Za-z]{4}$/.test(rest[0]) ? rest[1] : rest[0];
  return /^JP$/i.test(region ?? '');
};

/** Whether this browser's traffic should be treated as originating in Japan. */
export const isJapanTraffic = () =>
  JAPAN_TIME_ZONES.has(resolvedTimeZone()) || acceptedLanguages().some(hasJapanRegion);

export default isJapanTraffic;
